import type { Editor as TEditor } from "@tiptap/core";
import type { Transaction } from "@tiptap/pm/state";
import { Mic, Pause, Play, Square, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { appendFileToNote, insertFiles } from "./Editor";

/*
 * Evernote's audio recording: a bar at the bottom of the window records from the microphone
 * (MediaRecorder) and, on Stop, inserts the recording at the caret as an audio attachment with the usual
 * player. Works in the Android app too (RECORD_AUDIO in the manifest; Capacitor asks for the permission).
 */

const TYPES = ["audio/webm;codecs=opus", "audio/mp4", "audio/ogg;codecs=opus", "audio/webm"];
const EXT: Record<string, string> = { "audio/webm": "webm", "audio/mp4": "m4a", "audio/ogg": "ogg" };

function pickType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  return TYPES.find((t) => MediaRecorder.isTypeSupported(t)) ?? "";
}

const two = (n: number) => String(n).padStart(2, "0");
const clock = (ms: number) => `${Math.floor(ms / 60000)}:${two(Math.floor(ms / 1000) % 60)}`;

function fileName(type: string) {
  const d = new Date();
  const base = type.split(";")[0]!;
  return `Voice note ${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())} ${two(d.getHours())}-${two(d.getMinutes())}.${EXT[base] ?? "webm"}`;
}

function Recorder({ onDone, onClose }: { onDone: (f: File) => void; onClose: () => void }) {
  const [state, setState] = useState<"starting" | "recording" | "paused" | "error">("starting");
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const rec = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const keep = useRef(false);
  const clockRef = useRef({ total: 0, since: 0 });

  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer = 0;
    let ctx: AudioContext | null = null;
    let stopped = false;
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error("This browser can't record audio.");
        stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
        if (stopped) return stream.getTracks().forEach((t) => t.stop());
        const type = pickType();
        const r = new MediaRecorder(stream, type ? { mimeType: type, audioBitsPerSecond: 64000 } : undefined);
        rec.current = r;
        r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
        r.onstop = () => {
          stream?.getTracks().forEach((t) => t.stop());
          if (!keep.current || !chunks.current.length) return onClose();
          const mime = (r.mimeType || type || "audio/webm").split(";")[0]!;
          onDone(new File(chunks.current, fileName(mime), { type: mime }));
          onClose();
        };
        r.start(1000);
        clockRef.current = { total: 0, since: performance.now() };
        setState("recording");
        // A small level meter, so it's clear the microphone hears you.
        ctx = new AudioContext();
        const an = ctx.createAnalyser();
        an.fftSize = 512;
        ctx.createMediaStreamSource(stream).connect(an);
        const buf = new Uint8Array(an.fftSize);
        const tick = () => {
          an.getByteTimeDomainData(buf);
          let peak = 0;
          for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
          setLevel(peak / 128);
          const c = clockRef.current;
          setElapsed(c.total + (r.state === "recording" ? performance.now() - c.since : 0));
        };
        // A timer, not requestAnimationFrame: the clock must keep counting in a background tab.
        timer = window.setInterval(tick, 100);
      } catch (e) {
        setState("error");
        setError(e instanceof DOMException && e.name === "NotAllowedError" ? "Microphone access was blocked. Allow it in the browser's site settings." : e instanceof Error ? e.message : "Can't record audio.");
      }
    })();
    return () => {
      stopped = true;
      clearInterval(timer);
      void ctx?.close();
      if (rec.current && rec.current.state !== "inactive") rec.current.stop();
      else stream?.getTracks().forEach((t) => t.stop());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stop = (save: boolean) => {
    keep.current = save;
    const r = rec.current;
    if (r && r.state !== "inactive") r.stop();
    else onClose();
  };
  const pause = () => {
    const r = rec.current;
    if (!r) return;
    const c = clockRef.current;
    if (r.state === "recording") {
      r.pause();
      c.total += performance.now() - c.since;
      setState("paused");
    } else if (r.state === "paused") {
      r.resume();
      c.since = performance.now();
      setState("recording");
    }
  };

  return (
    <div className="recorder" role="dialog" aria-label="Recording audio" onKeyDown={(e) => e.key === "Escape" && stop(false)}>
      {state === "error" ? (
        <>
          <Mic size={16} className="recorder-mic off" />
          <span className="recorder-error">{error}</span>
          <button className="icon-btn" title="Close" aria-label="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </>
      ) : (
        <>
          <span className={`recorder-dot${state === "recording" ? " live" : ""}`} />
          <span className="recorder-time">{clock(elapsed)}</span>
          <span className="recorder-level" aria-hidden>
            <span style={{ transform: `scaleX(${state === "recording" ? Math.min(1, level * 2.2) : 0})` }} />
          </span>
          <span className="recorder-label">{state === "starting" ? "Starting…" : state === "paused" ? "Paused" : "Recording"}</span>
          <button className="icon-btn" title={state === "paused" ? "Resume" : "Pause"} disabled={state === "starting"} onClick={pause}>
            {state === "paused" ? <Play size={16} /> : <Pause size={16} />}
          </button>
          <button className="btn small primary recorder-stop" disabled={state === "starting"} onClick={() => stop(true)}>
            <Square size={12} fill="currentColor" /> Stop
          </button>
          <button className="icon-btn" title="Cancel recording" aria-label="Cancel recording" onClick={() => stop(false)}>
            <X size={16} />
          </button>
        </>
      )}
    </div>
  );
}

let open: (() => void) | null = null;

/** Insert → Audio recording: records, then the file lands where the caret was. */
export function recordAudio(editor: TEditor, noteId: string) {
  if (open) return; // one recorder at a time
  // Follows edits made while recording, so the file lands where the caret was, not mid-paragraph.
  const at = { pos: editor.state.selection.from };
  const follow = ({ transaction }: { transaction: Transaction }) => {
    at.pos = transaction.mapping.map(at.pos);
  };
  editor.on("transaction", follow);
  const host = document.createElement("div");
  document.body.append(host);
  const root = createRoot(host);
  const close = () => {
    editor.off("transaction", follow);
    root.unmount();
    host.remove();
    open = null;
  };
  open = close;
  root.render(
    <Recorder
      onClose={close}
      onDone={(file) => {
        // Left the note while recording: the recording still goes into it (at the end), never thrown away.
        if (editor.isDestroyed) void appendFileToNote(noteId, file);
        else void insertFiles(editor, noteId, [file], Math.min(at.pos, editor.state.doc.content.size));
      }}
    />,
  );
}
